import { useEffect, useRef, useState } from "react";

const MODEL_URL = "/models/vosk-model-small-tr-0.3.tar.gz";
const fold = (t) =>
    (t || "")
        .toLocaleLowerCase("tr-TR")
        .replace(/[çğıöşü]/g, (c) => ({ ç: "c", ğ: "g", ı: "i", ö: "o", ş: "s", ü: "u" })[c]);
// Offline small TR model hears "Hey DentDefter" mostly as "defter"; Chrome gives "dent defter".
const WAKE_WORDS = new Set(["defter", "dentdefter", "tefter", "depter", "deprem"]);
const isWake = (text) => {
    const f = fold(text);
    if (/dent\s*defter/.test(f)) return true;
    return f.split(/\s+/).some((w) => WAKE_WORDS.has(w));
};

const NativeSR = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
export const wakeEngine = () => (NativeSR && !window.dentdefter?.isDesktop ? "native" : "offline");

let modelPromise = null;
function loadModel() {
    if (!modelPromise) {
        modelPromise = import("vosk-browser").then(({ createModel }) => createModel(MODEL_URL));
        modelPromise.catch(() => {
            modelPromise = null;
        });
    }
    return modelPromise;
}

function startNative(onWake, onState) {
    const sr = new NativeSR();
    sr.lang = "tr-TR";
    sr.continuous = true;
    sr.interimResults = true;
    let alive = true;
    sr.onresult = (e) => {
        for (let i = e.resultIndex; i < e.results.length; i++) {
            if (isWake(e.results[i][0].transcript)) {
                alive = false;
                sr.abort();
                onWake();
                return;
            }
        }
    };
    sr.onerror = (e) => {
        if (e.error === "not-allowed" || e.error === "service-not-allowed") {
            alive = false;
            onState("error");
        }
    };
    sr.onend = () => alive && setTimeout(() => alive && sr.start(), 250);
    sr.start();
    onState("listening");
    return () => {
        alive = false;
        sr.abort();
    };
}

async function startOffline(onWake, onState) {
    onState("loading");
    const model = await loadModel();
    const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
    });
    const ctx = new AudioContext();
    const rec = new model.KaldiRecognizer(ctx.sampleRate);
    let fired = false;
    const check = (text) => {
        if (!fired && isWake(text)) {
            fired = true;
            onWake();
        }
    };
    rec.on("partialresult", (m) => check(m.result.partial));
    rec.on("result", (m) => check(m.result.text));
    const node = ctx.createScriptProcessor(4096, 1, 1);
    node.onaudioprocess = (e) => {
        try {
            rec.acceptWaveform(e.inputBuffer);
        } catch {
            /* recognizer busy */
        }
    };
    const src = ctx.createMediaStreamSource(stream);
    src.connect(node);
    node.connect(ctx.destination);
    onState("listening");
    return () => {
        src.disconnect();
        node.disconnect();
        stream.getTracks().forEach((t) => t.stop());
        ctx.close();
        rec.remove?.();
    };
}

export function useWakeWord({ enabled, paused, onWake }) {
    const [state, setState] = useState("off");
    const wakeRef = useRef(onWake);
    wakeRef.current = onWake;

    useEffect(() => {
        if (!enabled || paused) {
            setState(enabled ? "paused" : "off");
            return undefined;
        }
        let stop = null;
        let cancelled = false;
        const fire = () => wakeRef.current();
        const start = wakeEngine() === "native" ? async () => startNative(fire, setState) : () => startOffline(fire, setState);
        start()
            .then((s) => (cancelled ? s() : (stop = s)))
            .catch(() => !cancelled && setState("error"));
        return () => {
            cancelled = true;
            stop?.();
        };
    }, [enabled, paused]);

    return state;
}
